import MessagesClient, {AssistantMessageStatus, ConversationMessageType} from '@/client/mentor/MessagesClient'
import type {MaybeRef} from 'vue'
import type {Maybe} from '@/types'
import type {MessagesInfiniteData} from '@/composables/query/mentor/useAssistantMessages'
import type {Message} from '@/client/mentor/MessagesClient'
import {
  PENDING_STATE_CHECKER_BACKGROUND_POLLING_MS,
  PENDING_STATE_CHECKER_FOREGROUND_POLLING_MS,
} from '@/components/assistant/constants'
import {buildMessageQueryKey} from '@/composables/query/mentor/useAssistantMessages'
import {computed, toValue, watch} from 'vue'
import {isNullOrUndefined} from '@/utils/utils'
import {storeToRefs} from 'pinia'
import {useAssistantStore} from '@/stores/assistant'
import {useQueryClient} from '@tanstack/vue-query'
import {useTimeoutPoll} from '@vueuse/core'
import {replaceMessage} from '@/composables/query/mentor/useAssistantSendMessage'

export function usePendingStateChecker(
  companyId: MaybeRef<Maybe<number>>,
  options?: {
    onPendingMessageResolved?: () => void | Promise<void>
  }
) {
  const assistantStore = useAssistantStore()
  const queryClient = useQueryClient()

  const {activeConversationId, isVisible} = storeToRefs(assistantStore)

  const conversationMessageIds: Map<string, number> = new Map()

  const updateTimeout = computed(() =>
    assistantStore.isVisible ? PENDING_STATE_CHECKER_FOREGROUND_POLLING_MS : PENDING_STATE_CHECKER_BACKGROUND_POLLING_MS
  )
  useTimeoutPoll(backgroundCheckMessages, updateTimeout)

  watch(
    activeConversationId,
    (newConversationId, oldConversationId) => {
      if (oldConversationId) onConversationOutOfFocus(oldConversationId)
      if (newConversationId) onConversationFocussed(newConversationId)
    },
    {
      immediate: false,
    }
  )
  watch(
    isVisible,
    (newIsVisible) => {
      const conversationId = activeConversationId.value
      if (!conversationId) return

      if (newIsVisible) onConversationFocussed(conversationId)
      else onConversationOutOfFocus(conversationId)
    },
    {
      immediate: false,
    }
  )

  async function backgroundCheckMessages() {
    if (conversationMessageIds.size === 0) return

    const resolvedCompanyId = toValue(companyId)
    if (isNullOrUndefined(resolvedCompanyId)) return
    const newMessages = await MessagesClient.getBatchMessages(resolvedCompanyId, Array.from(conversationMessageIds.values()))
    let hasResolvedPendingMessage = false

    const latestConversationIds = new Set(conversationMessageIds.keys())
    conversationMessageIds.clear()
    newMessages.forEach((message: Message) => {
      if (!latestConversationIds.has(message.conversationId)) return

      if (message.type === ConversationMessageType.ASSISTANT && message.content.status === AssistantMessageStatus.PENDING) {
        conversationMessageIds.set(message.conversationId, message.id)
      } else {
        hasResolvedPendingMessage = true

        const resolvedCompanyId = toValue(companyId)
        if (!isNullOrUndefined(resolvedCompanyId)) {
          const queryKey = buildMessageQueryKey(resolvedCompanyId, message.conversationId)
          queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey, (data) => {
            if (!data) return data
            replaceMessage(data, message.id, message)
          })
        }
        if (!assistantStore.isVisible || assistantStore.activeConversationId !== message.conversationId) {
          assistantStore.unreadConversationIds.add(message.conversationId)
        }
      }
    })

    if (hasResolvedPendingMessage) {
      await options?.onPendingMessageResolved?.()
    }
  }

  function onConversationFocussed(conversationId: string) {
    conversationMessageIds.delete(conversationId)
  }

  function onConversationOutOfFocus(conversationId: string) {
    const resolvedCompanyId = toValue(companyId)
    if (isNullOrUndefined(resolvedCompanyId)) return

    const queryKey = buildMessageQueryKey(resolvedCompanyId, conversationId)
    const cachedData = queryClient.getQueryData<MessagesInfiniteData>(queryKey)

    if (cachedData && cachedData.pages) {
      const allMessages = cachedData.pages.flatMap((page) => page.items)
      const lastMessage = allMessages.reduce((max, msg) => (msg.id > max.id ? msg : max))

      if (
        lastMessage &&
        lastMessage.type === ConversationMessageType.ASSISTANT &&
        lastMessage.content.status === AssistantMessageStatus.PENDING
      ) {
        conversationMessageIds.set(conversationId, lastMessage.id)
      }
    }
  }

  return {
    onConversationFocussed,
    onConversationOutOfFocus,
  }
}
